import { maxBy } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useFullscreen } from '../../layouts/StackedLayout/StackedLayout';
import StaticSummaryGroup, { type StaticSummaryGroupItem } from './shared/StaticSummaryGroup';
import { createRadialLabelTickRenderer, type ML360Statistic } from './shared/utils';

const wordWrapBreakPoint = 400;

interface ML360ResultProps {
    statistics: ML360Statistic[];
    summaryFormation?: number[];
}

const ML360Result = ({ statistics, summaryFormation = [2, 2, 1] }: ML360ResultProps) => {
    const { t } = useTranslation('assessmentResultManagementPage');
    const { isMobile } = useFullscreen();
    const containerRef = useRef<HTMLDivElement>(null);
    const [wrapWords, setWrapWords] = useState(false);

    const domain = useMemo(() => {
        return maxBy(statistics, 'total')?.total || 0;
    }, [statistics]);

    const summaryItems: StaticSummaryGroupItem[] = useMemo(
        () => statistics.map(s => ({ title: `${s.value}/${s.total}`, description: s.label })),
        [statistics]
    );

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const width = entry.contentRect.width;
                setWrapWords(width < wordWrapBreakPoint);
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <div>
            <div className="mx-auto grid max-w-3xl gap-5">
                {/* Statistics */}
                <div className="w-full">
                    <StaticSummaryGroup formation={summaryFormation} items={summaryItems} isMobile={isMobile} />
                </div>

                {/* Results */}
                <div ref={containerRef} className="flex w-full flex-col items-center justify-center">
                    <div className={`w-full ${isMobile ? 'aspect-3/2 max-h-[38vh]' : 'aspect-16/10 max-h-[50vh]'}`}>
                        <ResponsiveContainer>
                            <RadarChart
                                outerRadius="75%"
                                data={statistics}
                                margin={{
                                    top: 8,
                                    right: isMobile ? 44 : 64,
                                    bottom: isMobile ? 24 : 32,
                                    left: isMobile ? 44 : 64,
                                }}
                            >
                                <PolarGrid strokeDasharray="3 3" stroke="#b7b8b9" />
                                <PolarAngleAxis
                                    dataKey="shortLabel"
                                    tick={createRadialLabelTickRenderer(isMobile ? 12 : 16, {
                                        fontSize: 12,
                                        fill: 'var(--color-neutral-500)',
                                        fontWeight: 500,
                                        wrapWords,
                                    })}
                                    stroke="#2221212c"
                                />
                                <PolarRadiusAxis
                                    angle={90}
                                    domain={[0, domain]}
                                    tick={{
                                        fontSize: 11,
                                        fill: 'var(--color-neutral-500)',
                                        dy: 0,
                                    }}
                                    axisLine={{ stroke: '#dce1ebff' }}
                                    tickLine={{ stroke: '#f13bbbff' }}
                                    stroke="#bff00eff"
                                    tickCount={8}
                                    tickFormatter={value => value.toString()}
                                    orientation="middle"
                                />
                                <Radar
                                    name={t('details.ml360.yourScore')}
                                    dataKey="value"
                                    stroke="var(--color-teal-600)"
                                    fill="#1fac7d56"
                                    fillOpacity={0.2}
                                    strokeWidth={2.5}
                                    dot={true}
                                    activeDot={{ r: 5 }}
                                />
                                <Tooltip
                                    formatter={(value: string) => [value, t('details.ml360.yourScore')]}
                                    labelFormatter={label => `${t('details.ml360.category')}: ${label}`}
                                    contentStyle={{
                                        borderRadius: '8px',
                                        backgroundColor: 'white',
                                        borderColor: '#c7c5c5ff',
                                        boxShadow: 'var(--shadow)',
                                        color: 'hsl(var(--foreground))',
                                        padding: '0.75rem 1rem',
                                    }}
                                />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ML360Result;
